import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import {
  BASE_SUBCATEGORIES,
  PRICING_CONFIG,
  PRICING_GROUPS,
  type PricingConfig,
  type PricingGroup,
  getBaseSubcategoryData,
  getUncategorizedBases,
} from 'element-domain'
import { useCallback, useEffect, useState } from 'react'

import { textValueComponent } from '@salsita-ai/platform-project-sdk/domain'

import { adminInterface } from '../../systems/admin-interface'

type PricingSidebarProps = {
  readonly entityId: string
}

export const PricingSidebarElement = (props: PricingSidebarProps) => {
  const { entityId } = props

  const { storeSystem } = adminInterface.useDomain()
  const [activeTab, setActiveTab] = useState(0)

  const entityData = adminInterface.useStoreSelector(
    emission => {
      const entity = emission.state.entities.find(e => e.id === entityId)
      if (!entity || !('data' in entity)) return ''
      return String((entity as unknown as { data: string }).data)
    },
    [entityId]
  )

  const [pricing, setPricing] = useState<PricingConfig>(() => parsePricing(entityData))

  useEffect(() => {
    setPricing(parsePricing(entityData))
  }, [entityData])

  const save = useCallback(() => {
    storeSystem.stageAndCommit([
      adminInterface.updates.ComponentModification({
        componentInstance: {
          entityId: entityId as Parameters<
            typeof adminInterface.updates.ComponentModification
          >[0]['componentInstance']['entityId'],
          componentType: textValueComponent,
          instanceName: 'data',
        },
        writableState: { value: JSON.stringify(pricing) },
        updateTag: 'pricing-settings-update',
      }),
    ])
  }, [storeSystem, entityId, pricing])

  const resetToDefaults = useCallback(() => {
    setPricing(PRICING_CONFIG)
  }, [])

  const updateTopField = useCallback(
    (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => {
      setPricing(prev => {
        const currentTop = prev.tops[topKey] ?? { basePrice: 0, compoundingRate: 0.05, pricingStep: 6 }
        return { ...prev, tops: { ...prev.tops, [topKey]: { ...currentTop, [field]: value } } } as PricingConfig
      })
    },
    []
  )

  const updateSimpleField = useCallback((category: string, key: string, value: number) => {
    setPricing(prev => {
      const current = prev[category as keyof PricingConfig]
      if (typeof current === 'object' && current !== null) {
        return { ...prev, [category]: { ...(current as Record<string, number>), [key]: value } } as PricingConfig
      }
      return prev
    })
  }, [])

  const updateScalarField = useCallback((field: 'grooves' | 'cornerRadius' | 'hasCrossbrace', value: number) => {
    setPricing(prev => ({ ...prev, [field]: value }) as PricingConfig)
  }, [])

  const group = PRICING_GROUPS[activeTab]

  return (
    <Stack gap={2} sx={{ p: 1 }}>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button size="small" onClick={resetToDefaults}>
          Reset Defaults
        </Button>
        <Button variant="contained" size="small" onClick={save}>
          Save
        </Button>
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_: React.SyntheticEvent, v: number) => {
          setActiveTab(v)
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {PRICING_GROUPS.map(g => (
          <Tab key={g.label} label={g.label} sx={{ minWidth: 0, px: 1.5, fontSize: '0.8rem' }} />
        ))}
      </Tabs>

      {group && (
        <GroupEditor
          group={group}
          pricing={pricing}
          onUpdateTop={updateTopField}
          onUpdateSimple={updateSimpleField}
          onUpdateScalar={updateScalarField}
        />
      )}
    </Stack>
  )
}

function parsePricing(data: string): PricingConfig {
  try {
    return data ? ({ ...PRICING_CONFIG, ...JSON.parse(data) } as PricingConfig) : PRICING_CONFIG
  } catch {
    return PRICING_CONFIG
  }
}

function GroupEditor({
  group,
  pricing,
  onUpdateTop,
  onUpdateSimple,
  onUpdateScalar,
}: {
  readonly group: PricingGroup
  readonly pricing: PricingConfig
  readonly onUpdateTop: (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => void
  readonly onUpdateSimple: (category: string, key: string, value: number) => void
  readonly onUpdateScalar: (field: 'grooves' | 'cornerRadius' | 'hasCrossbrace', value: number) => void
}) {
  return (
    <Stack gap={1}>
      {group.sections.map(section => {
        if (section.type === 'tops') {
          return <TopsPricingEditor key={section.category} tops={pricing[section.category]} onUpdate={onUpdateTop} />
        }

        if (section.type === 'scalar') {
          const value = pricing[section.category] as number
          return (
            <Accordion key={section.category}>
              <AccordionSummary>
                <Typography variant="subtitle2">{section.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <NumberField
                  label={section.label}
                  value={value}
                  onChange={v => {
                    onUpdateScalar(section.category as 'grooves' | 'cornerRadius' | 'hasCrossbrace', v)
                  }}
                />
              </AccordionDetails>
            </Accordion>
          )
        }

        const data = pricing[section.category] as Record<string, number>

        if (section.category === 'bases') {
          return (
            <BasesEditor
              key="bases"
              data={data}
              onUpdate={(key, value) => {
                onUpdateSimple('bases', key, value)
              }}
            />
          )
        }

        return (
          <SimplePricingEditor
            key={section.category}
            title={section.label}
            category={section.category}
            data={data}
            onUpdate={onUpdateSimple}
            step={section.category === 'woodSpecies' ? 0.01 : 1}
          />
        )
      })}
    </Stack>
  )
}

function BasesEditor({
  data,
  onUpdate,
}: {
  readonly data: Record<string, number>
  readonly onUpdate: (key: string, value: number) => void
}) {
  const uncategorized = getUncategorizedBases(data)

  return (
    <>
      {Object.entries(BASE_SUBCATEGORIES).map(([subcategory]) => {
        const subData = getBaseSubcategoryData(data, subcategory)
        if (Object.keys(subData).length === 0) return null
        return (
          <Accordion key={subcategory}>
            <AccordionSummary>
              <Typography variant="subtitle2">{subcategory}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack gap={1}>
                {Object.entries(subData).map(([key, value]) => (
                  <NumberField
                    key={key}
                    label={key}
                    value={value}
                    onChange={v => {
                      onUpdate(key, v)
                    }}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      })}

      {Object.keys(uncategorized).length > 0 && (
        <Accordion>
          <AccordionSummary>
            <Typography variant="subtitle2">Other</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={1}>
              {Object.entries(uncategorized).map(([key, value]) => (
                <NumberField
                  key={key}
                  label={key}
                  value={value}
                  onChange={v => {
                    onUpdate(key, v)
                  }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}

function TopsPricingEditor({
  tops,
  onUpdate,
}: {
  readonly tops: Record<string, { basePrice: number; compoundingRate: number; pricingStep: number }>
  readonly onUpdate: (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => void
}) {
  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="subtitle2">Top Shapes</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2}>
          {Object.entries(tops).map(([key, val]) => (
            <Box key={key}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {key}
              </Typography>
              <Stack direction="row" gap={1}>
                <NumberField
                  label="Base Price"
                  value={val.basePrice}
                  onChange={v => {
                    onUpdate(key, 'basePrice', v)
                  }}
                />
                <NumberField
                  label="Rate"
                  value={val.compoundingRate}
                  onChange={v => {
                    onUpdate(key, 'compoundingRate', v)
                  }}
                  step={0.01}
                />
                <NumberField
                  label="Step"
                  value={val.pricingStep}
                  onChange={v => {
                    onUpdate(key, 'pricingStep', v)
                  }}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function SimplePricingEditor({
  title,
  category,
  data,
  onUpdate,
  step = 1,
}: {
  readonly title: string
  readonly category: string
  readonly data: Record<string, number>
  readonly onUpdate: (category: string, key: string, value: number) => void
  readonly step?: number
}) {
  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1}>
          {Object.entries(data).map(([key, value]) => (
            <NumberField
              key={key}
              label={key}
              value={value}
              onChange={v => {
                onUpdate(category, key, v)
              }}
              step={step}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
}: {
  readonly label: string
  readonly value: number
  readonly onChange: (value: number) => void
  readonly step?: number
}) {
  return (
    <TextField
      label={label}
      type="number"
      size="small"
      value={value}
      onChange={e => {
        onChange(Number(e.target.value))
      }}
      inputProps={{ step }}
      fullWidth
    />
  )
}
